/**
 * Unified notification fan-out.
 *
 * The app historically sent notifications only to Telegram. This module adds an
 * optional Firebase *push* channel alongside it, chosen by the user via a single
 * setting: appSettings.notificationChannel = 'telegram' | 'push' | 'both'.
 *
 * Default is 'telegram' → existing behaviour is 100% preserved for anyone who
 * hasn't changed the setting. Push is purely additive/opt-in.
 *
 * ISOLATED: it only calls the backend /api/push/send relay. It does not touch
 * Supabase data. Never throws (fire-and-forget).
 */
import { getAuthToken, supabase } from '../config/supabase';

/** 'telegram' (default) | 'push' | 'both'. */
export const getNotificationChannel = () => {
  try {
    const raw = localStorage.getItem('appSettings');
    if (raw) {
      const c = JSON.parse(raw).notificationChannel;
      if (c === 'push' || c === 'both' || c === 'telegram') return c;
    }
  } catch (_) {}
  return 'telegram';
};

// Same per-type gating Telegram uses, so a type the user disabled stays silent
// on BOTH channels. speed_alert / geo_* have their own dedicated toggles.
const typeEnabled = (type) => {
  if (!type || type === 'speed_alert' || type === 'geo_entry' || type === 'geo_leave') return true;
  try {
    const raw = localStorage.getItem('appSettings');
    if (raw) {
      const s = JSON.parse(raw);
      if (s.telegramNotifications && s.telegramNotifications[type] === false) return false;
    }
  } catch (_) {}
  return true;
};

const stripHtml = (s) =>
  String(s || '').replace(/<[^>]+>/g, '').replace(/[ \t]+\n/g, '\n').replace(/\n{3,}/g, '\n\n').trim();

/**
 * Send a Firebase push to THIS agency's registered devices — but only if the
 * user enabled the push channel ('push' or 'both'). Fire-and-forget.
 * @param {string} message  the same text sent to Telegram (HTML is stripped).
 * @param {string} type     notification type key (for per-type gating + payload).
 */
export const maybeSendPush = async (message, type = '') => {
  const channel = getNotificationChannel();
  if (channel !== 'push' && channel !== 'both') return;
  if (!typeEnabled(type)) return;

  try {
    let companyName = 'CAR TRACK';
    try {
      const raw = localStorage.getItem('appSettings');
      if (raw) { const s = JSON.parse(raw); if (s.companyName) companyName = s.companyName; }
    } catch (_) {}

    // Scope the push to this tenant so devices of other agencies are never hit.
    let agencyId = (typeof window !== 'undefined' && window.__AGENCY_ID__) || null;
    let uid = null;
    if (!agencyId) {
      try { const { data } = await supabase.auth.getUser(); uid = data?.user?.id || null; } catch (_) {}
    }

    const title = stripHtml(message).split('\n')[0].slice(0, 80) || companyName;
    const body = stripHtml(message).slice(0, 240);

    const authToken = await getAuthToken();
    await fetch('/api/push/send', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
      },
      body: JSON.stringify({
        title: companyName,
        body: title !== body ? `${body}` : body,
        data: type ? { type } : {},
        ...(agencyId ? { agency_id: agencyId } : {}),
        ...(uid ? { uid } : {}),
      }),
    });
  } catch (err) {
    console.error('Push notification error:', err);
  }
};
